import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiBaseService } from './api-base.service';
import { HttpParams } from '@angular/common/http';

export interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
  type: 'file' | 'folder';
  id?: number;
  path?: string;
}

export interface FileNode {
  id?: number;
  name: string;
  type: 'file' | 'folder';
  path?: string;
  size?: number;
  modifiedDate?: string;
  children?: FileNode[];
}

export interface FileManagerListResponse {
  status: any;
  data: {
    basePath: string;
    paths: {
      type: 'FILE' | 'FOLDER';
      name: string;
    }[];
  };
}

export interface ClearanceProfile {
  code: string;
  label: string;
  enabled: boolean;
  loading: boolean;
}

interface FileManagerAclEntry {
  fileManagerAclId: number;
  path: string;
  permission: string;
  profile: string;
  status: 'ALLOWED' | 'FORBIDDEN';
}

const PROFILE_LABELS: Record<string, string> = {
  'ADM1': 'Administrator',
  'CTO1': 'CTO',
  'COO1': 'COO',
  'SAL1': 'Sales Director',
  'SAL2': 'Sales Senior',
  'SAL3': 'Sales',
  'CUS1': 'Customer Owner',
  'CUS2': 'Customer PM',
  'CUS3': 'Customer Administration',
  'TES1': 'Senior Tester',
  'TES2': 'Junior Tester',
  'TES3': 'Tester',
  'SOF3': 'Software Engineer',
  'MAT1': 'Mathematician',
  'COM4': 'Compliance',
  'SOF4': 'Software Engineer L4',
  'MAT4': 'Mathematician L4',
  'REG4': 'Regulatory L4',
  'TES5': 'Tester L5',
  'TES6': 'Tester L6',
  'MAN1': 'Manager',
  'MAN2': 'Manager L2',
  'DIR3': 'Director',
  'DIR4': 'Director L4',
  'TE6+': 'Tech Lead',
  'AUD1': 'Inspector / Auditor',
  'AUD2': 'Inspector Access',
  'AUD3': 'Auditor',
  'INT': 'Introducer',
  'HR1': 'HR Manager',
  'HR2': 'HR Senior',
  'HR3': 'HR',
  'OFF4': 'Office L4',
  'OFF5': 'Office L5',
  'PRJ6': 'Project Manager',
  'SEC1': 'Security',
  'CIS1': 'CIS',
  'IT01': 'IT',
  'QM01': 'Quality Manager',
  'HRD1': 'HR Director',
  'CCO': 'CCO',
  'HIT1': 'Head of IT',
  'HMT1': 'HMT',
  'HRA1': 'HRA',
  'CEO1': 'CEO',
  'CFO1': 'CFO',
  'GM01': 'General Manager'
};


const BASE_URL = '/file-manager';

@Injectable({
  providedIn: 'root'
})
export class FileManagerService extends ApiBaseService {

  actionFileCreate(file: File, params: HttpParams): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.POSTMULTIPART(this.getUrl(`${BASE_URL}/files`), formData, params);
  }

  actionFileDetail(params: HttpParams): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/files/url`), params);
  }

  actionFileRename(params: HttpParams): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/files/rename`), {}, params);
  }

  actionFileMove(params: HttpParams): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/files/move`), {}, params);
  }

  actionFileDelete(params: HttpParams): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/files`), params);
  }

  actionFolderFileList(params: HttpParams): Observable<FileManagerListResponse> {
    return this.GET(this.getUrl(`${BASE_URL}/files`), params);
  }

  actionFolderRename(params: HttpParams): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/folders/rename`), {}, params);
  }

  actionFolderMove(params: HttpParams): Observable<any> {
    return this.PUT(this.getUrl(`${BASE_URL}/folders/move`), {}, params);
  }

  actionFolderDelete(params: HttpParams): Observable<any> {
    return this.DELETE(this.getUrl(`${BASE_URL}/folders`), params);
  }

  actionFolderCreate(params: HttpParams): Observable<any> {
    return this.POST(this.getUrl(`${BASE_URL}/folders`), {}, params);
  }

  getClearanceProfiles(nodePath: string): Observable<ClearanceProfile[]> {
    const params = new HttpParams().set('path', nodePath);
    return this.GET(this.getUrl(`${BASE_URL}/acl/READ/profiles`), params).pipe(
      map((response: { data: { fileManagerAcls: FileManagerAclEntry[] } }) =>
        response.data.fileManagerAcls.map(acl => ({
          code: acl.profile,
          label: PROFILE_LABELS[acl.profile] ?? acl.profile,
          enabled: acl.status === 'ALLOWED',
          loading: false
        }))
      )
    );
  }

  updateClearanceProfile(nodePath: string, profileCode: string, enabled: boolean): Observable<void> {
    const status = enabled ? 'ALLOWED' : 'FORBIDDEN';
    const params = new HttpParams().set('path', nodePath);
    return this.POST(this.getUrl(`${BASE_URL}/acl/READ/profiles/${profileCode}/${status}`), {}, params);
  }
}
